import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnimatePresence, motion } from 'framer-motion';
import { useSFX } from 'js/hooks/useSFX';

import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';

// actions
import { updateCollection } from 'js/store/collections/Collections.actions';

// import selectors
import { getCollectionById } from 'js/store/collections/Collections.selectors';
import SwiperGrid from 'js/components/swiper/Swiper.grid';
import ActivityCard from 'js/components/activity-card/ActivityCard';
import ButtonWithIcon from 'js/components/button/Button.with-icon';
import { getTranslationById } from 'js/store/app/App.selectors';

// icons
import IconEdit from 'js/components/icon/Icon.edit';

// import components
import ButtonTickState from 'js/components/button/Button.tick-state';
import { trackEvent } from '../../../utils/tracking';

const CollectionEdit = ({ collectionID, closeModal }) => {
  const siteText = {
    editThisGallery: useSelector((state) =>
      getTranslationById(state, 'overlay_popup_edit_this_gallery', 'Edit this gallery')
    ),
    saveButtonText: useSelector((state) => getTranslationById(state, 'overlay_popup_save_button_text', 'Save')),
  };
  const dispatch = useDispatch();
  const { playSFX } = useSFX();

  const collection = useSelector((state) => getCollectionById(state, collectionID));
  const { cards = [] } = collection || {};

  // state
  const [showEditName, setShowEditName] = useState(false);
  const [removeCards, setRemoveCards] = useState([]);
  const [changeNameSelected, setChangeNameSelected] = useState(collection?.name || '');

  const currentLocalisedName = useSelector((state) =>
    getTranslationById(state, changeNameSelected, changeNameSelected)
  );

  // save changes
  const handleSaveChanges = () => {
    removeCards.forEach((cardId) => {
      trackEvent('game_collection_remove_own', { content_id: cardId });
    });
    dispatch(updateCollection(collectionID, changeNameSelected, removeCards));
    closeModal();
  };

  const handleBackButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowEditName((prevShowEditName) => {
      if (prevShowEditName) {
        return false;
      } else {
        closeModal();
        return prevShowEditName;
      }
    });
  };

  useEffect(() => {
    // hijack the control of the back button to close name dropdown if open
    const backButton = document.getElementById('back-button');
    backButton.addEventListener('click', handleBackButtonClick);
    return () => {
      backButton.removeEventListener('click', handleBackButtonClick);
    };
  }, []);

  return (
    <div className="collection-modal__edit">
      <h1 style={{ marginBottom: 30 }}>{siteText.editThisGallery}</h1>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <button
          className="edit-name"
          onClick={() => {
            setShowEditName(!showEditName);
            playSFX('button_tap');
          }}
        >
          <h3
            style={{
              textDecoration: 'underline',
              color: '#fff',
              marginRight: 10,
            }}
          >
            {currentLocalisedName}
          </h3>
          <IconEdit color={'#fff'} />
        </button>

        <AnimatePresence mode="wait">
          {showEditName && (
            <motion.div
              key={`edit-state-name`}
              variants={variants}
              initial="initial"
              animate="animate"
              exit="exit"
              style={{
                position: 'absolute',
                top: 0,
                left: '50%',
                width: '100%',
                height: '57vh',
                backgroundColor: '#fff',
                zIndex: 10,
                borderRadius: '36px',
                overflow: 'hidden',
                transform: 'translateX(-50%)',
              }}
            >
              <EditCollectionName
                currentName={changeNameSelected}
                setChangeNameSelected={(name) => {
                  setChangeNameSelected(name);
                  setShowEditName(false);
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <EditCollectionCards cards={cards} removeCards={removeCards} setRemoveCards={setRemoveCards} />
      <ButtonWithIcon
        title={siteText.saveButtonText}
        iconName={'tick'}
        iconPosition={'right'}
        style={{
          backgroundColor: '#9DC145',
          borderRadius: '36px',
          margin: 'auto',
          marginTop: '30px',
          padding: '15px 20px 15px 50px',
          fontSize: 14,
        }}
        action={handleSaveChanges}
      />
    </div>
  );
};

export default CollectionEdit;

const EditCollectionCards = ({ cards, removeCards, setRemoveCards }) => {
  return (
    <SwiperGrid
      slides={cards.map((card) => {
        return (
          <div style={{ position: 'relative' }} key={card.id}>
            <div className={`card__save-action`}>
              <ButtonTickState
                action={() => {
                  if (removeCards.includes(card.id)) {
                    setRemoveCards(removeCards.filter((id) => id !== card.id));
                  } else {
                    setRemoveCards([...removeCards, card.id]);
                  }
                }}
                selected={!removeCards.includes(card.id)}
              />
            </div>
            <ActivityCard allowFlip={false} data={card} />
          </div>
        );
      })}
      grid={true}
      width={185}
    />
  );
};

const EditCollectionName = ({ currentName, setChangeNameSelected }) => {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        backgroundColor: '#fff',
        padding: 20,
      }}
    >
      <OverlayScrollbarsComponent
        defer
        className="custom-scrollbar collection-modal__edit__names"
        style={{ height: '100%' }}
      >
        {Array.from({ length: 20 }, (_, index) => {
          const name = `gallery_name_${index + 1}`;
          return (
            <div
              key={index}
              onClick={() => {
                setChangeNameSelected(name);
                playSFX('button_tap');
              }}
              className={`collection-modal__create-name` + (currentName === name ? ' selected' : '')}
              style={{
                padding: '5px 5px 5px 15px',
                margin: '0px 25px 10px',
                color: currentName === name ? '#9DC145' : '#000',
                cursor: 'pointer',
                textAlign: 'start',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {useSelector((state) => getTranslationById(state, `gallery_name_${index + 1}`, `Gallery ${index + 1}`))}
              <ButtonTickState selected={currentName === name} style={{ marginLeft: 'auto', borderColor: '#003764' }} />
            </div>
          );
        })}
      </OverlayScrollbarsComponent>
    </div>
  );
};

const variants = {
  initial: {
    height: 0,
    x: '-50%',
  },
  animate: {
    height: '57vh',
    x: '-50%',
    transition: {
      type: 'spring',
      stiffness: 120,
      damping: 18,
      mass: 0.8,
    },
  },
  exit: {
    height: 0,
    x: '-50%',
    transition: {
      duration: 0.3,
      ease: 'easeIn',
    },
  },
};
